// v3.15-927: Schedule Appointment Modal — opened from a client's detail page
// header. Pre-fills the rep (current user) and client.
// v3.21-1624 · Added Assigned Rep + Additional Team Members.  Availability
// recalculates against the SELECTED rep's real calendar via the same
// authoritative `/appointments/availability/{repId}` endpoint used
// elsewhere in the Hub.  Additional participants are recorded on the
// appointment payload (`additional_participant_ids`) so the existing
// server-side notification / audit path can fan out invites; no fake
// intersection of calendars is invented.
import { useEffect, useMemo, useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Loader2, Clock, Calendar as CalendarIcon, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import ClientLocalTime from './ClientLocalTime';

const toLocalYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const fmtTime = (iso) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

// v3.21-1624 · Compact inline picker backed by the internal-only
// `/team-chats/users/search` endpoint (partner roles + inactive users
// filtered out server-side, so we can't accidentally offer them).
function InlineEmployeePicker({ label, onPick, testId, exclude = [] }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const term = (q || '').trim();
    if (term.length < 2) { setRows([]); return undefined; }
    const h = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get('/team-chats/users/search', { params: { q: term, limit: 12 } });
        setRows(r.data?.users || []);
      } catch { setRows([]); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(h);
  }, [q, open]);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener('mousedown', h);
    return () => window.removeEventListener('mousedown', h);
  }, [open]);
  const exSet = new Set(exclude);
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="text-[11px] text-slate-800 border border-slate-300 rounded px-2 py-0.5 hover:bg-slate-100"
        data-testid={`${testId}-open`}>
        {label}
      </button>
      {open && (
        <div className="absolute z-40 mt-1 w-64 rounded border border-slate-200 bg-white shadow-lg p-2"
             data-testid={`${testId}-panel`}>
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search employees…"
            className="w-full text-sm rounded border border-slate-300 px-2 py-1"
            data-testid={`${testId}-search`} />
          <div className="mt-1 max-h-52 overflow-y-auto">
            {loading && <div className="text-xs text-slate-500 py-1">searching…</div>}
            {!loading && q.trim().length >= 2 && rows.length === 0 &&
              <div className="text-xs text-slate-500 py-1">No employees found</div>}
            {rows.filter((u) => !exSet.has(u.id)).map((u) => (
              <button key={u.id} type="button"
                onClick={() => { onPick(u); setOpen(false); setQ(''); }}
                className="w-full text-left rounded px-2 py-1 hover:bg-slate-100"
                data-testid={`${testId}-pick-${u.id}`}>
                <div className="text-sm font-medium truncate">{u.full_name || u.email}</div>
                <div className="text-[10px] text-slate-500 truncate">{u.email}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ScheduleAppointmentModal({ open, onOpenChange, client, currentUser, onScheduled }) {
  const [date, setDate] = useState(toLocalYMD(new Date()));
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  // v3.21-1624 · Assigned Rep + collaborators.
  const [assignedRep, setAssignedRep] = useState(null);   // {id, full_name}
  const [participants, setParticipants] = useState([]);   // [{id, full_name}]

  const repId = assignedRep?.id;

  useEffect(() => {
    if (!open) {
      setDate(toLocalYMD(new Date()));
      setSlots([]);
      setSelected(null);
      setNotes('');
      setParticipants([]);
      setAssignedRep({
        id: currentUser?.user_id || currentUser?.id,
        full_name: currentUser?.full_name || currentUser?.email,
      });
    }
  }, [open, currentUser?.user_id, currentUser?.id, currentUser?.full_name, currentUser?.email]);

  // v3.21-1624 · Availability MUST recalculate whenever the Assigned Rep
  // changes.  Same authoritative endpoint used by the public /book page.
  useEffect(() => {
    if (!open || !repId || !date) return;
    let alive = true;
    setLoading(true);
    setSelected(null);
    setSlots([]);   // clear stale slots immediately
    (async () => {
      try {
        const { data } = await api.get(`/appointments/availability/${repId}`, { params: { date } });
        if (alive) setSlots(data?.slots || []);
      } catch {
        if (alive) setSlots([]);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [open, repId, date]);

  const quickDate = (offset) => {
    const d = new Date();
    d.setDate(d.getDate() + offset);
    setDate(toLocalYMD(d));
  };

  const submit = async () => {
    if (!selected) { toast.error('Please pick a time slot'); return; }
    setSaving(true);
    try {
      await api.post('/appointments', {
        client_id: client.id,
        assigned_to: repId,
        date_time_start: selected.start,
        date_time_end:   selected.end,
        notes,
        // v3.21-1624 · Optional additional participants — persisted on
        // the appointment so downstream notification/audit fan-out
        // (existing architecture) can see them.
        additional_participant_ids:   participants.map((p) => p.id),
        additional_participant_names: participants.map((p) => p.full_name),
      });
      toast.success(`Appointment scheduled with ${assignedRep?.full_name || 'rep'}`);
      // v3.21-1788 · Universal Ghost live refresh — dispatch the
      // canonical event bus so GhostDailyOrganizer (and any other
      // subscriber) refetches its unified feed IMMEDIATELY without a
      // page reload.  This is the same in-SPA CustomEvent pattern the
      // Hub already uses for `client-workspace:open`.
      try {
        window.dispatchEvent(new CustomEvent('ghost:refresh', {
          detail: { reason: 'appointment_created' },
        }));
      } catch (_e) { /* noop */ }
      onScheduled?.();
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.admin_force_required) {
        toast.error('Conflicts with another appointment (including buffer). Admin override required.');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to schedule');
      }
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]" data-testid="schedule-appointment-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarIcon className="h-4 w-4 text-indigo-600" />
            Schedule Appointment
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded px-3 py-2 flex items-center justify-between gap-2">
            <div>
              With <span className="font-semibold text-slate-800">{client?.full_name}</span>
            </div>
            {client?.id && <ClientLocalTime clientId={client.id} variant="chip" />}
          </div>

          {/* v3.21-1624 · Assigned Rep row */}
          <div className="text-xs bg-white border border-slate-200 rounded px-3 py-2 flex flex-wrap items-center gap-2"
               data-testid="schedule-assigned-rep-row">
            <span className="text-slate-500 font-medium">Assigned Rep:</span>
            <span className="font-semibold text-slate-800" data-testid="schedule-assigned-rep">
              {assignedRep?.full_name || '—'}
            </span>
            <InlineEmployeePicker
              label="Change"
              testId="schedule-rep-picker"
              onPick={(u) => setAssignedRep({ id: u.id, full_name: u.full_name })}
            />
          </div>

          {/* v3.21-1624 · Additional participants (invitees; no fake
              free/busy intersection — availability tracks Assigned Rep). */}
          <div className="text-xs bg-white border border-slate-200 rounded px-3 py-2 flex flex-wrap items-center gap-2"
               data-testid="schedule-participants-row">
            <span className="text-slate-500 font-medium">Also include:</span>
            {participants.map((p) => (
              <span key={p.id} className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 flex items-center gap-1"
                    data-testid={`schedule-participant-${p.id}`}>
                {p.full_name}
                <button type="button" onClick={() => setParticipants((prev) => prev.filter((x) => x.id !== p.id))}
                        className="text-slate-500 hover:text-red-600">×</button>
              </span>
            ))}
            <InlineEmployeePicker
              label="+ Add"
              testId="schedule-participant-picker"
              exclude={[assignedRep?.id, ...participants.map((p) => p.id)].filter(Boolean)}
              onPick={(u) => setParticipants((prev) => prev.some((x) => x.id === u.id) ? prev : [...prev, { id: u.id, full_name: u.full_name }])}
            />
            <span className="text-[10px] text-slate-400 ml-auto">Availability tracks the Assigned Rep&apos;s calendar</span>
          </div>

          <div>
            <label className="text-[11px] font-medium text-slate-600 block mb-1">Date</label>
            <Input
              type="date"
              value={date}
              min={toLocalYMD(new Date())}
              onChange={(e) => setDate(e.target.value)}
              data-testid="schedule-date"
            />
            <div className="flex gap-1.5 mt-2">
              <button type="button" onClick={() => quickDate(0)} className="h-7 text-[11px] px-2.5 rounded border border-slate-200 bg-slate-50 hover:bg-slate-100" data-testid="schedule-quick-today">Today</button>
              <button type="button" onClick={() => quickDate(1)} className="h-7 text-[11px] px-2.5 rounded border border-slate-200 bg-slate-50 hover:bg-slate-100" data-testid="schedule-quick-tomorrow">Tomorrow</button>
              <button type="button" onClick={() => quickDate(7)} className="h-7 text-[11px] px-2.5 rounded border border-slate-200 bg-slate-50 hover:bg-slate-100" data-testid="schedule-quick-nextweek">Next Week</button>
            </div>
          </div>

          <div>
            <label className="text-[11px] font-medium text-slate-600 block mb-1">
              Available Slots
              <span className="ml-1 text-[10px] text-slate-400">(for {assignedRep?.full_name || 'selected rep'})</span>
            </label>
            {loading ? (
              <div className="text-xs text-slate-400 py-4 flex items-center justify-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Loading…</div>
            ) : slots.length === 0 ? (
              <div className="text-xs text-slate-400 py-4 text-center border border-dashed border-slate-200 rounded" data-testid="schedule-no-slots">
                No open slots on this day. Try another date.
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5 max-h-48 overflow-y-auto" data-testid="schedule-slots-grid">
                {slots.map((s) => (
                  <button
                    key={s.start}
                    type="button"
                    onClick={() => setSelected(s)}
                    className={`h-8 text-xs rounded-md border transition ${selected?.start === s.start ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white border-slate-200 hover:border-indigo-400 text-slate-700'}`}
                    data-testid={`schedule-slot-${s.start.slice(11,16).replace(':','')}`}
                  >
                    {fmtTime(s.start)}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="text-[11px] font-medium text-slate-600 block mb-1">Notes (optional)</label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="schedule-notes" />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button
            type="button"
            onClick={submit}
            disabled={saving || !selected}
            className="bg-indigo-600 hover:bg-indigo-500"
            data-testid="schedule-submit-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Clock className="h-4 w-4 mr-1" />}
            {selected ? `Confirm — ${fmtTime(selected.start)}` : 'Select a slot'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

